import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const normalizeSearchValue = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLocaleLowerCase('es-MX');

const defaultAliases = () => [];

const SearchableCatalogSelect = ({
  name,
  value,
  options = [],
  onChange,
  getOptionValue,
  getOptionLabel,
  getOptionAliases = defaultAliases,
  placeholder = 'Buscar…',
  disabled = false,
  allowCustom = false,
  customValuePattern,
  enterAsTab = false,
  className = '',
  inputClassName = '',
}) => {
  const generatedId = useId().replace(/:/g, '');
  const listboxId = `searchable-catalog-${generatedId}`;
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listboxRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [listboxPosition, setListboxPosition] = useState({
    top: 0,
    left: 0,
    width: 240,
    maxHeight: 240,
    openAbove: false,
  });

  const catalog = useMemo(() => options.map((option) => ({
    option,
    value: String(getOptionValue(option) ?? ''),
    label: String(getOptionLabel(option) ?? ''),
    aliases: getOptionAliases(option).map(normalizeSearchValue).filter(Boolean),
  })), [getOptionAliases, getOptionLabel, getOptionValue, options]);

  const selected = useMemo(
    () => catalog.find((item) => item.value === String(value ?? '')),
    [catalog, value]
  );
  const [text, setText] = useState(selected?.label || '');

  const filteredCatalog = useMemo(() => {
    const search = normalizeSearchValue(text);
    if (!search || selected?.label === text) return catalog;
    return catalog.filter((item) => (
      normalizeSearchValue(item.label).includes(search)
      || item.aliases.some((alias) => alias.includes(search))
    ));
  }, [catalog, selected?.label, text]);

  const updateListboxPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, 240);
    const expectedHeight = Math.min(240, Math.max(44, filteredCatalog.length * 40));
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openAbove = spaceBelow < Math.min(expectedHeight, 160) && spaceAbove > spaceBelow;
    const availableHeight = openAbove ? spaceAbove : spaceBelow;
    const viewportLeft = Math.min(
      Math.max(8, rect.left),
      Math.max(8, window.innerWidth - width - 8)
    );

    setListboxPosition({
      top: (openAbove ? rect.top - 4 : rect.bottom + 4) + window.scrollY,
      left: viewportLeft + window.scrollX,
      width,
      maxHeight: Math.min(240, Math.max(80, availableHeight)),
      openAbove,
    });
  }, [filteredCatalog.length]);

  useEffect(() => {
    setText(selected?.label || (value == null ? '' : String(value)));
  }, [selected?.label, value]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      const clickedInField = containerRef.current?.contains(event.target);
      const clickedInListbox = listboxRef.current?.contains(event.target);
      if (!clickedInField && !clickedInListbox) setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateListboxPosition();
    window.addEventListener('resize', updateListboxPosition);
    window.addEventListener('scroll', updateListboxPosition, true);
    return () => {
      window.removeEventListener('resize', updateListboxPosition);
      window.removeEventListener('scroll', updateListboxPosition, true);
    };
  }, [isOpen, updateListboxPosition]);

  const resolveOption = (input) => {
    const normalized = normalizeSearchValue(input);
    if (!normalized) return null;
    return catalog.find((item) => (
      normalizeSearchValue(item.label) === normalized
      || item.aliases.includes(normalized)
    )) || null;
  };

  const selectOption = (item) => {
    setText(item.label);
    setIsOpen(false);
    setActiveIndex(-1);
    onChange?.(item.value);
  };

  const handleChange = (event) => {
    const nextText = event.target.value;
    const match = resolveOption(nextText);
    setText(nextText);
    setIsOpen(true);
    setActiveIndex(-1);

    if (match) {
      onChange?.(match.value);
    } else if (allowCustom && (!customValuePattern || customValuePattern.test(nextText.trim()))) {
      onChange?.(nextText.trim());
    } else if (value !== '' && value != null) {
      onChange?.('');
    }
  };

  const handleBlur = () => {
    const match = resolveOption(text);
    const customValue = text.trim();
    const customIsValid = allowCustom
      && customValue
      && (!customValuePattern || customValuePattern.test(customValue));
    setText(match?.label || (customIsValid ? customValue : (selected?.label || '')));
    if (enterAsTab) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        if (filteredCatalog.length === 0) return -1;
        if (current < 0) return direction > 0 ? 0 : filteredCatalog.length - 1;
        return (current + direction + filteredCatalog.length) % filteredCatalog.length;
      });
      return;
    }
    if (event.key === 'Enter' && isOpen && activeIndex >= 0 && filteredCatalog[activeIndex]) {
      if (!enterAsTab) event.preventDefault();
      selectOption(filteredCatalog[activeIndex]);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        name={name}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={(event) => {
          event.target.select();
          setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        className={`w-full rounded-lg border border-slate-300 px-3 py-2 pr-8 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 ${inputClassName}`}
      />
      {!disabled && isOpen && ReactDOM.createPortal(
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          className="z-50 min-w-[15rem] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          style={{
            position: 'absolute',
            top: `${listboxPosition.top}px`,
            left: `${listboxPosition.left}px`,
            width: `${listboxPosition.width}px`,
            maxHeight: `${listboxPosition.maxHeight}px`,
            transform: listboxPosition.openAbove ? 'translateY(-100%)' : undefined,
            zIndex: 9999,
          }}
        >
          {filteredCatalog.length > 0 ? filteredCatalog.map((item, index) => (
            <button
              key={item.value}
              type="button"
              role="option"
              aria-selected={item.value === String(value ?? '')}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption(item)}
              className={`block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-blue-50 ${index === activeIndex ? 'bg-blue-50' : ''}`}
            >
              {item.label}
            </button>
          )) : (
            <div className="px-3 py-2 text-sm text-slate-500">Sin coincidencias</div>
          )}
        </div>,
        document.body
      )}
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" aria-hidden="true">⌄</span>
    </div>
  );
};

export default SearchableCatalogSelect;
